import { tWithKeyAttr as t } from "../../i18nHelper";
import loginStyles from "./login.module.css";
import { KeyboardEvent, useCallback, useEffect, useRef, useState } from "react"; 
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../services/hooks";
import { errorUserSelector } from "../../services/selectors/auth.selectors";
import {
  authApi,
  useLoginGoogleMutation,
  useSendCodeWithoutLoginMutation,
} from "../../services/api/auth.api";
import Tooltip from "../../components/tooltip/tooltip";
import { useGoogleLogin } from "@react-oauth/google";
import { TLoginGoogleRequest } from "../../services/types/auth.types";
import { userSelector } from "../../services/selectors/user.selectors";
import BackArrowButton from "../../components/backArrowButton/backArrowButton";
import { useLazyGetUserQuery } from "../../services/api/user.api";

const OTP_LENGTH = 6;
const INITIAL_STATE = ["", "", "", "", "", ""];

function LoginSmsCode() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/main";
  const therapist = useAppSelector(userSelector);
  const loginError = useAppSelector(errorUserSelector);

  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  const [nums, setNums] = useState(INITIAL_STATE);

  const [showSendCodeTooltip, setShowSendCodeTooltip] = useState(false);
  const [isSendDisabled, setIsSendDisabled] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [otpDebug, setOtpDebug] = useState<string[]>([]);

  const [sendCode] = authApi.useSendCodeMutation();
  const [sendTokenToServer] = useLoginGoogleMutation();
  const [authWithoutLogin] = authApi.useAuthWithoutLoginMutation();
  const [getUser] = useLazyGetUserQuery();
  const [sendCodeWithoutLogin] = useSendCodeWithoutLoginMutation();

  const loginWithGoogle = useGoogleLogin({
    onSuccess: (codeResponse) => {
      sendTokenToServer(codeResponse as TLoginGoogleRequest);
    },
  });

  useEffect(() => {
    if (!therapist.id && !therapist.phone) {
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to focus on the next input
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    const { key } = e;
    const currentInput = inputRefs[index].current;
    if (!currentInput) return;

    if (key >= "0" && key <= "9") {
      e.preventDefault(); 
      const newNums = [...nums];
      newNums[index] = key;
      setNums(newNums);

      if (index < inputRefs.length - 1) {
        inputRefs[index + 1].current?.focus();
      }
    }

    if (key === "Backspace") {
      e.preventDefault();
      const newNums = [...nums];

      if (newNums[index]) {
        newNums[index] = "";
        setNums(newNums);
      } else if (index > 0) {
        newNums[index - 1] = "";
        setNums(newNums);
        inputRefs[index - 1].current?.focus();
      }
    }

    if (key === "ArrowRight") {
      if (index < inputRefs.length - 1) {
        inputRefs[index + 1].current?.focus();
      }
    }

    if (key === "ArrowLeft") {
      if (index > 0) {
        inputRefs[index - 1].current?.focus();
      }
    }
  };
  const fillCode = useCallback((code: string, source: string) => {
    const digits = code.replace(/\D/g, "").slice(0, OTP_LENGTH).split("");
    if (digits.length === 0) return;
    const newNums = [...INITIAL_STATE];
    digits.forEach((d, i) => { newNums[i] = d; });
    setNums(newNums);
    inputRefs[Math.min(digits.length, OTP_LENGTH - 1)].current?.focus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const hasWebOTP = "OTPCredential" in window;
    if (!hasWebOTP) return;
    const ac = new AbortController();
    navigator.credentials
      .get({
        // @ts-expect-error Web OTP API is not in standard TypeScript lib yet
        otp: { transport: ["sms"] },
        signal: ac.signal,
      })
      .then((otp: unknown) => {
        const code = (otp as { code?: string })?.code;
        if (code) fillCode(code, "WebOTP");
      })
      .catch((err) => {
        // console.error(`Web OTP error: ${err?.name} - ${err?.message}`);
      });
    return () => ac.abort();
  }, [fillCode]);

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").replace(/\D/g, "");
    if (pasteData.length > 0) {
      fillCode(pasteData, "paste");
    }
  };

  const handleSubmit = () => {
    const code = nums.join("");
    const roleId = 2;

    const payload = therapist.id
      ? {
        code,
        usersId: therapist.id,
        roleId,
        email: therapist.email,
        password: therapist.password || "",
      }
      : { code, roleId, phone: therapist.phone || "" };

    const submitFn = therapist.id ? sendCode : authWithoutLogin;

    // @ts-ignore
    submitFn(payload).then((response) => {
      if ("data" in response && response.data?.success) {
        setNums(Array(6).fill(""));
        getUser().then(() => navigate(from, { replace: true }));
      } else {
        setNums(Array(6).fill(""));
        if (inputRefs[0].current) inputRefs[0].current.focus();
      }
    });
  };

  useEffect(() => {
    if (nums.every((num) => num !== "")) {
      setTimeout(() => {
        handleSubmit();
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nums]);

  const sendCodeAgain = () => {
    if (isSendDisabled) return;

    sendCodeWithoutLogin({
      phone: therapist.phone,
      roleId: therapist.roleId,
      code: "0",
    }).then((response) => {
      if ("data" in response && response.data.success) {
        setShowSendCodeTooltip(true);
        setIsSendDisabled(true);
        setCountdown(30);
      }
    });
    setTimeout(() => {
      setShowSendCodeTooltip(false);
    }, 1500);
    setTimeout(() => setIsSendDisabled(false), 30000);
  };

  useEffect(() => {
    if (countdown === null) return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev && prev > 1) return prev - 1;
        clearInterval(interval);
        setIsSendDisabled(false);
        return null;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [countdown]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length > 1) {
      fillCode(value, "autofill");
    } else if (value.length === 1) {
      const newNums = [...nums];
      newNums[index] = value;
      setNums(newNums);
      if (index < inputRefs.length - 1) {
        inputRefs[index + 1].current?.focus();
      }
    }
  };
  return (
    <form
      autoComplete="one-time-code"
      onSubmit={(e) => e.preventDefault()}
      className={loginStyles.login}
      data-testid="enter-verification-code-page"
    >
      <BackArrowButton
        text={t("LOGIN_SMS_BACK_BUTTON")}
        onClick={() => navigate("/")}
      />
      <h1
        className={`${loginStyles.login__title} ${loginStyles.login__title_sms}`}
      >
        {t("LOGIN_SMS_TITLE")}
      </h1>
      {therapist?.phone && (
        <p
          className={`${loginStyles.login__text} ${loginStyles.login__text_small}`}
        >
          {t("LOGIN_SMS_SENT_TEXT")}{therapist?.phone?.slice(-3)}
        </p>
      )}
      <label htmlFor="num1" className={loginStyles.login__enterCodeLabel}>
        {t("LOGIN_SMS_ENTER_CODE_LABEL")}
      </label>
      <ul className={loginStyles.login__enterCode}>
        {nums.map((num, index) => (
          <input
            key={index}
            ref={inputRefs[index]}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            type="text"
            className={loginStyles.login__number}
            placeholder="__"
            onChange={(e) => handleChange(e, index)}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={index === 0 ? "one-time-code" : "off"}
            name={`otp-${index}`}
            id={`otp-${index}`}
            value={nums[index]}
            autoFocus={index === 0}
          />
        ))}
      </ul>
      {loginError && (
        <p className={`${loginStyles.login__invalid_message}`}>{loginError}</p>
      )}
      <p className={loginStyles.login__sendAgain}>
        {t("LOGIN_SMS_NOT_RECEIVED_CODE")}
        <span
          className={`${loginStyles.login__sendAgain_underlined} hover`}
          onClick={sendCodeAgain}
          style={{
            pointerEvents: isSendDisabled ? "none" : "auto",
            opacity: isSendDisabled ? 0.6 : 1,
          }}
        >
          {t("LOGIN_SMS_RESEND")}
          <Tooltip isShown={showSendCodeTooltip}>
            {t("LOGIN_SMS_TOOLTIP")}
          </Tooltip>
        </span>
        {isSendDisabled && countdown !== null && (
          <span className={loginStyles.login__countdown}>
            {t("LOGIN_SMS_COUNTDOWN_TEXT_PART1")} {countdown} {t("LOGIN_SMS_COUNTDOWN_TEXT_PART2")}
          </span>
        )}
      </p>

      <div className={loginStyles.login__bottomBar}>
        <p className={loginStyles.login__alternative}>
          {t("LOGIN_SMS_ALTERNATIVE")}
        </p>
        <div className={loginStyles.login__social}>
          <ul
            className={`${loginStyles.login__socialList} ${loginStyles.login__socialList_column}`}
          >
            <li>
              <button
                type="button"
                onClick={() => loginWithGoogle()}
                className={`${loginStyles.login__socialBtn} ${loginStyles.login__socialBtn_long} hover`}
              >
                <span
                  className={`${loginStyles.login__socialBtn} ${loginStyles.login__socialBtn_small} ${loginStyles.login__socialBtn_google}`}
                />
                {t("LOGIN_SMS_GOOGLE")}
              </button>
            </li>
            <li>
              <button
                type="button"
                className={`${loginStyles.login__socialBtn} ${loginStyles.login__socialBtn_long} hover`}
              >
                <span
                  className={`${loginStyles.login__socialBtn} ${loginStyles.login__socialBtn_small} ${loginStyles.login__socialBtn_apple}`}
                />
                {t("LOGIN_SMS_APPLE")}              </button>
            </li>
            <li>
              <button
                type="button"
                className={`${loginStyles.login__socialBtn} ${loginStyles.login__socialBtn_long} hover`}
              >
                <span
                  className={`${loginStyles.login__socialBtn} ${loginStyles.login__socialBtn_small} ${loginStyles.login__socialBtn_facebook}`}
                />
                {t("LOGIN_SMS_FACEBOOK")}
              </button>
            </li>
          </ul>
        </div>
      </div>
      <div>
        <p className={loginStyles.login__conditions}>
          {t("LOGIN_SMS_USE_CONDITIONS")}
        </p>
        {/*<Link className={loginStyles.login__link} to="/terms">*/}
        {/*  בתקנון השימוש*/}
        {/*</Link>*/}
      </div>
    </form>
  );
}

export default LoginSmsCode;
